/**
 * Data Hub — Production Records tab.
 *
 * Read-only table of promoted timeseries with account/metric search.
 */
import { useMemo, useState } from "react";

import DropdownSelector from "../../Components/DropdownSelector";
import SearchInput from "../../Components/Search/SearchInput";
import Table from "../../Components/Table";
import { TabError, TabLoading } from "../components/TabState";
import { DATASET_LABELS } from "../data/dataset";
import type { ProductionRow } from "../data/types";
import { useActiveDataset } from "../hooks/useActiveDataset";
import { useAdapterData } from "../hooks/useAdapterData";
import { useDataHubAdapter } from "../hooks/useDataHubAdapter";

export function ProductionTab() {
  const [dataset] = useActiveDataset();
  const adapter = useDataHubAdapter();
  const { data, loading, error } = useAdapterData(
    () => adapter.getProductionRows(),
    [dataset]
  );
  const [scope, setScope] = useState("Account");
  const [term, setTerm] = useState("");

  const rows = useMemo(() => {
    let out = data ?? [];
    if (term.trim()) {
      const t = term.trim().toLowerCase();
      out = out.filter(r =>
        scope === "Account"
          ? r.account_name.toLowerCase().includes(t)
          : r.metric_name.toLowerCase().includes(t)
      );
    }
    return out;
  }, [data, term, scope]);

  if (loading) return <TabLoading label="Loading production records…" />;
  if (error) return <TabError error={error} />;

  const visible = rows.slice(0, 250);

  const columns = [
    {
      key: "measurement_date",
      title: "Date",
      width: "120px",
      render: (_: unknown, r: ProductionRow) => (
        <span className="whitespace-nowrap text-gray-600">
          {r.measurement_date}
        </span>
      ),
    },
    {
      key: "account_name",
      title: "Account",
      width: "160px",
      render: (_: unknown, r: ProductionRow) => (
        <span className="whitespace-nowrap">{r.account_name}</span>
      ),
    },
    {
      key: "metric_name",
      title: "Metric",
      width: "160px",
      render: (_: unknown, r: ProductionRow) => (
        <span className="whitespace-nowrap text-gray-700">{r.metric_name}</span>
      ),
    },
    {
      key: "category",
      title: "Category",
      width: "140px",
      render: (_: unknown, r: ProductionRow) => (
        <span className="text-gray-500">{r.category}</span>
      ),
    },
    {
      key: "value",
      title: "Value",
      width: "160px",
      textalign: "left" as const,
      render: (_: unknown, r: ProductionRow) => (
        <span className="tabular-nums">{r.value.toLocaleString()}</span>
      ),
    },
    {
      key: "normalized_value",
      title: "Normalized",
      width: "110px",
      textalign: "left" as const,
      render: (_: unknown, r: ProductionRow) => (
        <span className="tabular-nums text-gray-600">
          {r.normalized_value?.toFixed(2) ?? "—"}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="relative z-20 flex flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:flex-nowrap sm:items-center">
        <div className="min-w-0 shrink-0">
          <h3 className="m-0 text-base font-semibold text-gray-900">
            Production records
          </h3>
          <p className="m-0 text-xs text-gray-500">
            {visible.length.toLocaleString()} of {rows.length.toLocaleString()}{" "}
            shown
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:flex-nowrap">
          <DropdownSelector
            label="Field"
            selected={scope}
            options={[
              { label: "Account", value: "Account" },
              { label: "Metric", value: "Metric" },
            ]}
            onSelect={setScope}
          />
          <div className="w-[220px] shrink-0">
            <SearchInput
              value={term}
              onChange={e => setTerm(e.target.value)}
              placeholder="Search…"
            />
          </div>
        </div>
      </div>

      <div className="relative z-0 overflow-hidden">
        <Table
          className="[&_th]:normal-case [&_th]:tracking-normal"
          columns={columns}
          data={visible}
          rowKey={r => r.id}
          emptyContent={
            <p className="m-0 text-sm text-gray-500">
              No {DATASET_LABELS[dataset].toLowerCase()} production records.
            </p>
          }
          scrollable
          maxHeight={520}
        />
      </div>
    </div>
  );
}

export default ProductionTab;
